import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAdmin, type SubscriptionRow, type Tier } from '@/hooks/useAdmin'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { CreditCard, ChevronRight } from 'lucide-react'

function tierBadge(tier: Tier) {
  const map: Record<Tier, string> = {
    free: 'text-muted-foreground border-border',
    team: 'text-primary border-primary/40 bg-primary/10',
    scale: 'border-border bg-muted/50',
    enterprise: 'text-success border-success/40 bg-success/10',
  }
  return map[tier] ?? 'text-muted-foreground border-border'
}

function tierInlineStyle(tier: Tier): React.CSSProperties {
  if (tier === 'scale') return { color: 'hsl(var(--amber))' }
  return {}
}

function subStatusPill(status: string) {
  if (status === 'active') return 'status-pill status-pill-success'
  if (status === 'trialing') return 'status-pill status-pill-muted'
  if (status === 'past_due' || status === 'unpaid') return 'status-pill status-pill-danger'
  return 'status-pill status-pill-muted'
}

export function SubscriptionsPage() {
  const { listSubscriptions } = useAdmin()
  const [rows, setRows] = useState<SubscriptionRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setLoading(true)
    listSubscriptions()
      .then(({ subscriptions }) => setRows(subscriptions))
      .catch(() => setError('Failed to load subscriptions'))
      .finally(() => setLoading(false))
  }, [listSubscriptions])

  return (
    <div className="space-y-6">
      <div>
        <h1 className="brand-wordmark text-2xl">Subscriptions</h1>
        <p className="kicker text-muted-foreground mt-1">
          Billing status for every organization
        </p>
      </div>

      <Card>
        {error ? (
          <CardContent className="p-6 text-sm text-destructive">{error}</CardContent>
        ) : loading ? (
          <CardContent className="p-0">
            <div className="divide-y divide-border">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="flex items-center gap-4 px-6 py-3.5">
                  <div className="h-4 w-48 rounded bg-muted/40 animate-pulse" />
                  <div className="h-4 w-16 rounded bg-muted/30 animate-pulse" />
                  <div className="ml-auto h-4 w-24 rounded bg-muted/30 animate-pulse" />
                </div>
              ))}
            </div>
          </CardContent>
        ) : rows.length === 0 ? (
          <CardContent className="flex flex-col items-center gap-3 py-16 text-muted-foreground">
            <CreditCard className="h-8 w-8 opacity-40" />
            <p className="text-sm">No subscriptions found</p>
          </CardContent>
        ) : (
          <CardContent className="p-0">
            <div className="grid grid-cols-[1fr_100px_130px_160px_80px_80px_28px] gap-4 px-6 py-2.5 border-b text-xs font-medium text-muted-foreground uppercase tracking-wide">
              <span>Organization</span>
              <span>Tier</span>
              <span>Status</span>
              <span>Period ends</span>
              <span className="text-center">Customer</span>
              <span className="text-center">Subscription</span>
              <span />
            </div>
            <div className="divide-y divide-border">
              {rows.map((row) => (
                <Link
                  key={row.id}
                  to={`/orgs/${row.id}`}
                  className="grid grid-cols-[1fr_100px_130px_160px_80px_80px_28px] gap-4 px-6 py-3.5 items-center hover:bg-accent/40 transition-colors group"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{row.name}</p>
                    <p className="mono-accent text-muted-foreground truncate">{row.id}</p>
                  </div>
                  <div>
                    <Badge
                      variant="outline"
                      className={`text-xs capitalize ${tierBadge(row.tier)}`}
                      style={tierInlineStyle(row.tier)}
                    >
                      {row.tier}
                    </Badge>
                  </div>
                  <div>
                    <span className={subStatusPill(row.subscription_status)}>
                      {row.subscription_status || 'none'}
                    </span>
                  </div>
                  <p className="mono-accent text-muted-foreground text-sm">
                    {row.subscription_current_period_end
                      ? new Date(row.subscription_current_period_end).toLocaleDateString()
                      : '—'}
                  </p>
                  <div className="flex justify-center">
                    {row.has_customer ? (
                      <span className="status-pill status-pill-success">Yes</span>
                    ) : (
                      <span className="status-pill status-pill-muted">No</span>
                    )}
                  </div>
                  <div className="flex justify-center">
                    {row.has_subscription ? (
                      <span className="status-pill status-pill-success">Yes</span>
                    ) : (
                      <span className="status-pill status-pill-muted">No</span>
                    )}
                  </div>
                  <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/50 group-hover:text-muted-foreground transition-colors" />
                </Link>
              ))}
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  )
}
